import _ from 'lodash';
import moment from 'moment';
import constants from '$$helpers/constants';
import estimationsModule from '../../../components/estimations';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import timeTrackingModel from '$$timeTrackingModel';
import chartsConfig, { getCurrencyCode } from './chartsConfig';
import app from '$$app';
import reportHelpers from '../include/helpers';
import numbersHelper from '../../../helpers/numbers';
import { reportWorkerError } from '$$workerErrorReporting';
import globalToast from '$$vueCmp/toast';

const MONTH_LOCALES = [
  'report_month_jan',
  'report_month_feb',
  'report_month_march',
  'report_month_apr',
  'report_month_may',
  'report_month_jun',
  'report_month_jul',
  'report_month_aug',
  'report_month_sep',
  'report_month_oct',
  'report_month_nov',
  'report_month_dec',
];

const MONTH_IDS = ['jan', 'feb', 'march', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

class MonthBudgetReport {
  constructor() {
    this.costWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;
    this.totalRange = {
      start: null,
      end: null,
    };
    this.baselineTotalRange = {
      start: null,
      end: null,
    };
  }

  initCostWorker() {
    if (window.Worker && !this.costWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.costWorker = new Worker('/workers/costs_worker.js' + `?${version}`);
    }

    this._initEvents();
  }

  _initEvents() {
    const that = this;

    this.costWorker.onmessage = function (e) {
      const updatedCosts = e.data;

      Object.entries(updatedCosts).forEach(([key, updates]) => {
        if (updates.length) {
          const tasks = that.reportTasks[key]?.tasks;

          if (Array.isArray(tasks)) {
            that.reportTasks[key].tasks = tasks.map(task => {
              const update = updates.find(obj => obj.id === task.id);

              if (update) {
                return {
                  ...task,
                  cost: update.cost,
                  actual_cost: update.actual_cost,
                };
              }

              return task;
            });
          }
        }
      });

      that.calculateMonthBudgetReport();
    };

    this.costWorker.onerror = function (e) {
      reportWorkerError(e, 'costs_worker');
      globalToast.error(__('costs_calculating_error'));
    };
  }

  calculateCosts(tasks, baselineTasks, calculationMode) {
    this.calculationMode = calculationMode;
    this.reportTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];
    const defaultRange = reportHelpers.defaultRange().last_6_months;

    const totalEstimate = tasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);

    const tasksAndMilestones = tasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);
    const baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

    const range = {
      start: currentFilter?.properties?.rangeDate?.start || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || totalEstimate?.end_date || defaultRange.end,
    };

    const baselineRange = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || totalEstimate?.start_date || defaultRange.start,
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || totalEstimate?.end_date || defaultRange.end,
    };

    this.totalRange = { ...range };
    this.baselineTotalRange = { ...baselineRange };

    const preapredTasks = this.prepareTasksForWorker(tasksAndMilestones);
    const preapredBaselineTasks = this.prepareTasksForWorker(baselineTasksAndMilestones);
    const filteredTasks = this.returnTasksByCurrentFilter(preapredTasks, currentFilter);
    const filteredBaselineTasks = this.returnTasksByCurrentFilter(preapredBaselineTasks, currentFilter);
    const tasksByMonth = this.prepareMonthTasks(filteredTasks, filteredBaselineTasks, range);

    const reportTasks = {
      filteredCurrentTasks: { tasks: filteredTasks, range },
      filteredBaselineTasks: { tasks: filteredBaselineTasks, range: baselineRange },
      ...tasksByMonth,
    };

    this.reportTasks = reportTasks;
    this.sendDataToWorker(reportTasks);
  }

  prepareMonthTasks(tasks, baselineTasks, range) {
    const startDate = moment(range.start);
    const endDate = moment(range.end);
    const startMonth = startDate.clone().startOf('month');
    const endMonth = endDate.clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');
    const preparedTasksMap = {};

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(range.start).add(i, 'months');
      let firstDayOfCurrentMonth = currentMonth.clone().startOf('month');
      let lastDayOfCurrentMonth = currentMonth.clone().endOf('month');

      if (i === 0) {
        firstDayOfCurrentMonth = moment(range.start);
      }

      if (i === monthsDifference) {
        lastDayOfCurrentMonth = moment(range.end);
      }

      const tasksByMonth = tasks.filter(task => reportHelpers.returnTaskByRange(
        {
          ...task,
          start_date: task.task_data.start_date,
          end_date: task.task_data.end_date,
        }, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth },
      ));
      const baselineTasksByMonth = baselineTasks.filter(task => reportHelpers.returnTaskByRange({
        ...task,
        start_date: task.task_data.start_date,
        end_date: task.task_data.end_date,
      }, { start: firstDayOfCurrentMonth, end: lastDayOfCurrentMonth }));

      const id = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;

      const workerRange = {
        start: firstDayOfCurrentMonth.valueOf(),
        end: lastDayOfCurrentMonth.valueOf(),
      };

      preparedTasksMap[id] = { tasks: tasksByMonth, range: workerRange };
      preparedTasksMap[`${id}-baseline`] = { tasks: baselineTasksByMonth, range: workerRange };
    }

    return preparedTasksMap;
  }

  sendDataToWorker(tasksData) {
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.costWorker.postMessage(_.cloneDeep({
      projectConfigs: reportHelpers.prepareProjectConfigs(),
      projectSettings: projectSettingsMap,
      customDaysData: customDayTemplatesModel.serialize(),
      tasksData,
      resourcesData: Store.getters['resourcesModel/getAllResources'],
    }));
  }

  returnTasksByCurrentFilter(tasks, currentFilter) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    return tasks.filter(task => reportHelpers.returnTaskByRange({
      ...task,
      start_date: task.task_data.start_date,
      end_date: task.task_data.end_date,
    }, filterRange)).filter(task => {
      if (!currentFilter?.properties?.assignee?.length) {
        return task;
      }

      if (currentFilter.properties.assignee.includes('-1') && !task.resource_id.length) {
        return task;
      }

      const assigneeTask = task.resource_id.find(id => currentFilter.properties.assignee.includes(+id));

      if (assigneeTask) {
        return task;
      }
    }).filter(task => {
      if (!currentFilter?.properties?.text?.length) {
        return task;
      }

      return task.task_data.text.toLowerCase().startsWith(currentFilter.properties.text.toLowerCase());
    });
  }

  prepareTasksForWorker(tasks) {
    const resultData = [];

    _.each(tasks, taskData => {
      const projectData = projectsModel.getProjectDataById(taskData.gantt_id);
      const resources = Store.getters['resourcesModel/getResourcesByTaskIdAndGanttId'](taskData.gantt_id, taskData.id);
      const timeLogsData = timeTrackingModel.getLogsByParams(taskData.gantt_id, taskData.id);

      resultData.push({
        id: taskData.id,
        gantt_id: taskData.gantt_id,
        task_id: taskData.id, // do not remove
        project_name: projectData.name,
        task_name: taskData.text,
        task_data: taskData,
        info_text: taskData.note,
        resources,
        resource_id: _.map(resources, res => String(res.id)),
        resource_data: resources,
        time_logs: timeLogsData ? timeLogsData.sum : '0',
        cost: estimationsModule.calculateCostChildTask(taskData) || 0,
        actual_cost: estimationsModule.calculateActualCostChildTask(taskData) || 0,
        cost_range_start: taskData.start_date,
        cost_range_end: taskData.end_date,
        logsData: timeTrackingModel.getLogsByParams(taskData.gantt_id, taskData.id),
      });
    });

    return resultData;
  }

  calculateMonthBudgetReport() {
    if (!this.reportTasks?.filteredCurrentTasks?.tasks?.length) {
      app.trigger('onAfterMonthTimeReportCalculation', null);

      return;
    }

    let cumulativeCostResult = 0;
    let cumulativeBaselineCostResult = 0;
    let cumulativeActualCostResult = 0;

    const resultConfig = _.cloneDeep(chartsConfig.monthReport.default);
    const xAxisValues = [];

    const startMonth = moment(this.totalRange.start).clone().startOf('month');
    const endMonth = moment(this.totalRange.end).clone().startOf('month');
    const monthsDifference = endMonth.diff(startMonth, 'months');

    const isBaseline = this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE;

    for (let i = 0; i <= monthsDifference; i++) {
      const currentMonth = moment(this.totalRange.start).add(i, 'months');
      const dateString = `${MONTH_LOCALES[currentMonth.month()]} ${currentMonth.year()}`;

      xAxisValues.push(dateString);

      const dateKey = `${MONTH_IDS[currentMonth.month()]}-${currentMonth.year()}`;
      const baselineDateKey = `${dateKey}-baseline`;

      let tasksByMonth = isBaseline ? this.reportTasks[baselineDateKey].tasks : this.reportTasks[dateKey].tasks;

      if (this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
        tasksByMonth = this.reportTasks[dateKey].tasks;
        const baselineTasksByMonth = this.reportTasks[baselineDateKey].tasks;

        const currentMonthCost = tasksByMonth.reduce((sum, task) => sum + +task.cost, 0);
        const baselineCurrentMonthCost = baselineTasksByMonth.reduce((sum, task) => sum + +task.cost, 0);

        cumulativeCostResult += currentMonthCost;
        cumulativeBaselineCostResult += baselineCurrentMonthCost;

        resultConfig.series = resultConfig.series.filter(item => !['actualCost', 'cumActualCost'].includes(item.name)).map(item => {
          if (item.name === 'totalCost') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: currentMonthCost,
                  label: {
                    formatter: `${getCurrencyCode()} ${numbersHelper.getFormatted(+currentMonthCost.toFixed(2))}`,
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: currentMonthCost && typeof currentMonthCost === 'number' ? currentMonthCost.toFixed(2) : +currentMonthCost || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'totalBaselineCost') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: baselineCurrentMonthCost,
                  label: {
                    formatter: `${getCurrencyCode()} ${numbersHelper.getFormatted(+baselineCurrentMonthCost.toFixed(2))}`,
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: baselineCurrentMonthCost && typeof baselineCurrentMonthCost === 'number' ? baselineCurrentMonthCost.toFixed(2) : +baselineCurrentMonthCost || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumTotalCost') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: cumulativeCostResult,
                  customData: {
                    label: item.locale,
                    value: cumulativeCostResult && typeof cumulativeCostResult === 'number' ? cumulativeCostResult.toFixed(2) : +cumulativeCostResult || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumBaselineTotalCost') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: cumulativeBaselineCostResult,
                  customData: {
                    label: item.locale,
                    value: cumulativeBaselineCostResult && typeof cumulativeBaselineCostResult === 'number' ? cumulativeBaselineCostResult.toFixed(2) : +cumulativeBaselineCostResult || 0,
                  },
                },
              ],
            };
          }
        });
      } else {
        const currentMonthCost = tasksByMonth.reduce((sum, task) => sum + +task.cost, 0);
        const currentMonthActualCost = tasksByMonth.reduce((sum, task) => sum + +task.actual_cost, 0);

        cumulativeCostResult += currentMonthCost;
        cumulativeActualCostResult += currentMonthActualCost;

        let seriesItems = resultConfig.series.filter(item => !['totalBaselineCost', 'cumBaselineTotalCost'].includes(item.name));

        if (isBaseline) {
          seriesItems = resultConfig.series.filter(item => !['totalCost', 'cumTotalCost'].includes(item.name));
        }

        resultConfig.series = seriesItems.map(item => {
          if (item.name === 'totalCost' || item.name === 'totalBaselineCost') {
            const changeColor = {};

            if (isBaseline && item.name === 'totalBaselineCost') {
              changeColor.color = '#8BC34A';
            }

            return {
              ...item,
              ...changeColor,
              data: [
                ...item.data,
                {
                  value: currentMonthCost,
                  label: {
                    formatter: `${getCurrencyCode()} ${numbersHelper.getFormatted(+currentMonthCost.toFixed(2))}`,
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: currentMonthCost && typeof currentMonthCost === 'number' ? currentMonthCost.toFixed(2) : +currentMonthCost || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'actualCost') {
            return {
              ...item,
              data: [
                ...item.data,
                {
                  value: currentMonthActualCost,
                  label: {
                    formatter: `${getCurrencyCode()} ${numbersHelper.getFormatted(+currentMonthActualCost.toFixed(2))}`,
                    position: 'insideTop',
                  },
                  customData: {
                    label: item.locale,
                    value: currentMonthActualCost && typeof currentMonthActualCost === 'number' ? currentMonthActualCost.toFixed(2) : +currentMonthActualCost || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumTotalCost' || item.name === 'cumBaselineTotalCost') {
            const changeColor = {};

            if (isBaseline && item.name === 'cumBaselineTotalCost') {
              changeColor.color = '#6E8F3F';
            }

            return {
              ...item,
              ...changeColor,
              data: [
                ...item.data,
                {
                  value: cumulativeCostResult,
                  customData: {
                    label: item.locale,
                    value: cumulativeCostResult && typeof cumulativeCostResult === 'number' ? cumulativeCostResult.toFixed(2) : +cumulativeCostResult || 0,
                  },
                },
              ],
            };
          }

          if (item.name === 'cumActualCost') {
            return {
              ...item,
              customData: {
                label: item.locale,
                value: cumulativeCostResult && typeof cumulativeCostResult === 'number' ? cumulativeCostResult.toFixed(2) : +cumulativeCostResult || 0,
              },
              data: [
                ...item.data,
                {
                  value: cumulativeActualCostResult,
                  customData: {
                    label: item.locale,
                    value: cumulativeActualCostResult && typeof cumulativeActualCostResult === 'number' ? cumulativeActualCostResult.toFixed(2) : +cumulativeActualCostResult || 0,
                  },
                },
              ],
            };
          }
        });
      }
    }

    const barValues = resultConfig.series.filter(item => item.type === 'bar').flatMap(item => item.data.map(obj => obj.value));
    const lineValues = resultConfig.series.filter(item => item.type === 'line').flatMap(item => item.data.map(obj => obj.value));
    const intervals = reportHelpers.calculateYAxisIntervals(barValues, lineValues);

    if (intervals.left > 0 && intervals.right > 0) {
      resultConfig.xAxis[0].data = xAxisValues;
      resultConfig.yAxis[0].interval = intervals.left;
      resultConfig.yAxis[1].interval = intervals.right;

      app.trigger('onAfterMonthBubgetCalculation', resultConfig);

      return;
    }

    app.trigger('onAfterMonthBubgetCalculation', null);
  }
}

const monthBudgetReport = new MonthBudgetReport();

export default monthBudgetReport;
